import { initializeIcons } from "@fluentui/font-icons-mdl2";
import * as React from "react";
import { BrandVariants, createLightTheme, createDarkTheme, Theme, FluentProvider } from "@fluentui/react-components";
import { store } from "../store";
import * as Sentry from "@sentry/react";
import { SentryCustomData } from "../config";
import { Environment } from "../service/globalTypes";
import posthog from "posthog-js";
import { Provider } from "react-redux";
import Header from "./components/header";
import ReactDOM from "react-dom";
import { ThemeProvider } from "@fluentui/react";
import App from "./components/App";
import PostHogService from "../service/PostHogService";

Sentry.init({
  environment: process.env.REACT_APP_ENVIRONMENT,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({}),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

Sentry.setExtra(SentryCustomData.ENVIRONMENT, Environment.PRODUCTION);
Sentry.setExtra(SentryCustomData.CONNECTION_ESTABLISHED, false);
Sentry.setExtra(SentryCustomData.AUTO_UPDATE_ENABLED, false);

if (typeof window !== "undefined" && process.env.REACT_APP_POSTHOG_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",

    loaded: (posthog) => {
      if (process.env.REACT_APP_ENVIRONMENT === "development") {
        posthog.debug();
      }
    },

    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: false,
  });

  PostHogService.capture('office_addin_initialized', {
    environment: process.env.REACT_APP_ENVIRONMENT,
    timestamp: new Date().toISOString(),
    addin_type: "excel_taskpane",
  });
}

/* global document, Office, module, require */

const customtheme: BrandVariants = {
  10: "#020305",
  20: "#111723",
  30: "#16263D",
  40: "#193253",
  50: "#1B3F6A",
  60: "#1B4C82",
  70: "#18599B",
  80: "#1267B4",
  90: "#3174C2",
  100: "#4F82C8",
  110: "#6790CF",
  120: "#7D9ED5",
  130: "#92ACDC",
  140: "#A6BAE2",
  150: "#BAC9E9",
  160: "#CDD8EF",
};

const lightTheme: Theme = {
  ...createLightTheme(customtheme),
};

const darkTheme: Theme = {
  ...createDarkTheme(customtheme),
};

darkTheme.colorBrandForeground1 = customtheme[110];
darkTheme.colorBrandForeground2 = customtheme[120];

initializeIcons();

let isOfficeInitialized = false;

const title = "Contoso Task Pane Add-in";

const render = (Component: any) => {
  ReactDOM.render(
    <Provider store={store}>
      <Header />
      <ThemeProvider>
        <FluentProvider theme={lightTheme}>
          <Component title={title} isOfficeInitialized={isOfficeInitialized} />
        </FluentProvider>
      </ThemeProvider>
    </Provider>,
    document.getElementById("container")
  );
};

/* Render application after Office initializes */
Office.onReady(() => {
  isOfficeInitialized = true;
  render(App);
});
